// MetricsRow.jsx

import React, { useState, useMemo } from 'react';
import { db } from '../config/firebase';
import { collection, addDoc, updateDoc, deleteDoc, query, where, getDocs } from 'firebase/firestore';
import Modal from 'react-modal';
import { FaTrash } from 'react-icons/fa';
import Tooltip from './Tooltip';
import '../css/MetricsRow.css'; // Create appropriate styles

function MetricsRow({
  person,
  dailyMetricsData = [],
  selectedYear,
  selectedMonth,
  refreshData,
  firms = [],
  metrickePostavke = {}, // Receive metric settings
  daysInMonth = 35, // Receive daysInMonth
  isPinned,
  onTogglePin,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [workModalOpen, setWorkModalOpen] = useState(false);
  const [currentDay, setCurrentDay] = useState(null);
  const [dailyAmount, setDailyAmount] = useState(0);
  const [workDetails, setWorkDetails] = useState({
    fromTime: '',
    toTime: '',
    plannedWorkingHours: '',
    actualWorkingHours: '',
  });
  const [isRowSelected, setIsRowSelected] = useState(false);

  // Function to open modal for a specific day (earnings)
  const openModal = (day) => {
    const metric = dailyMetricsData.find((m) => m.day === day);
    setCurrentDay(day);
    setDailyAmount(metric ? parseFloat(metric.amount) : 0);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentDay(null);
    setDailyAmount(0);
  };

  // Function to save metric
  const handleSave = async () => {
    try {
      const metricsCollection = collection(db, 'dailyMetrics');
      const q = query(
        metricsCollection,
        where('userId', '==', person.id),
        where('year', '==', selectedYear),
        where('month', '==', selectedMonth),
        where('day', '==', currentDay)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Update existing metric
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, { amount: dailyAmount });
      } else {
        // Create new metric
        await addDoc(metricsCollection, {
          userId: person.id,
          year: selectedYear,
          month: selectedMonth,
          day: currentDay,
          amount: dailyAmount,
        });
      }

      alert('Metrika uspješno spremljena!');
      closeModal();
      refreshData();
    } catch (e) {
      console.error('Greška pri spremanju metrike:', e);
      alert('Došlo je do greške prilikom spremanja metrike. Pokušajte ponovno.');
    }
  };

  // Function to delete metric
  const handleDelete = async () => {
    if (window.confirm(`Jeste li sigurni da želite obrisati metriku za dan ${currentDay}?`)) {
      try {
        const metricsCollection = collection(db, 'dailyMetrics');
        const q = query(
          metricsCollection,
          where('userId', '==', person.id),
          where('year', '==', selectedYear),
          where('month', '==', selectedMonth),
          where('day', '==', currentDay)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const docRef = querySnapshot.docs[0].ref;
          await deleteDoc(docRef);
          alert('Metrika uspješno izbrisana!');
          closeModal();
          refreshData();
        }
      } catch (e) {
        console.error('Greška pri brisanju metrike:', e);
        alert('Došlo je do greške prilikom brisanja metrike. Pokušajte ponovno.');
      }
    }
  };

  // Function to open modal for entering work hours
  const openWorkModal = (day) => {
    const metric = dailyMetricsData.find((m) => m.day === day);
    setCurrentDay(day);
    setWorkDetails({
      fromTime: metric ? metric.fromTime || '' : '',
      toTime: metric ? metric.toTime || '' : '',
      plannedWorkingHours: metric ? metric.plannedWorkingHours || '' : '',
      actualWorkingHours: metric ? metric.actualWorkingHours || '' : '',
    });
    setWorkModalOpen(true);
  };

  const closeWorkModal = () => {
    setWorkModalOpen(false);
    setCurrentDay(null);
    setWorkDetails({
      fromTime: '',
      toTime: '',
      plannedWorkingHours: '',
      actualWorkingHours: '',
    });
  };

  // Save work hours
  const handleSaveWorkDetails = async () => {
    try {
      const metricsCollection = collection(db, 'dailyMetrics');
      const q = query(
        metricsCollection,
        where('userId', '==', person.id),
        where('year', '==', selectedYear),
        where('month', '==', selectedMonth),
        where('day', '==', currentDay)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Update existing metric with work hours
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, {
          plannedWorkingHours: workDetails.plannedWorkingHours,
          actualWorkingHours: workDetails.actualWorkingHours,
        });
      } else {
        // Create new metric with work hours
        await addDoc(metricsCollection, {
          userId: person.id,
          year: selectedYear,
          month: selectedMonth,
          day: currentDay,
          plannedWorkingHours: workDetails.plannedWorkingHours,
          actualWorkingHours: workDetails.actualWorkingHours,
        });
      }

      alert('Radno vrijeme uspješno spremljeno!');
      closeWorkModal();
      refreshData();
    } catch (e) {
      console.error('Greška pri spremanju radnog vremena:', e);
      alert('Došlo je do greške prilikom spremanja radnog vremena. Pokušajte ponovno.');
    }
  };

  // Function to get firm name based on firmId
  const getFirmName = (firmId) => {
    const firm = firms.find((firm) => firm.id === firmId);
    return firm ? firm.name : 'N/A';
  };

  // Function to determine cell class for daily metrics
  const getCellClass = (amount) => {
    if (person.nationality === 'Stranac') {
      if (amount < metrickePostavke.minDailyStranci) {
        return 'low-earning';
      }
    } else if (person.nationality === 'Hrvat') {
      if (amount < metrickePostavke.minDailyHrvati) {
        return 'low-earning';
      }
    }

    return '';
  };

  // Calculate monthly earnings for this user
  const monthlySum = useMemo(() => {
    return dailyMetricsData.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);
  }, [dailyMetricsData]);

  // Sum of actual working hours for this user
  const totalActualWorkingHours = useMemo(() => {
    return dailyMetricsData.reduce((sum, m) => {
      const hours = parseFloat(m.actualWorkingHours) || 0;
      return sum + hours;
    }, 0);
  }, [dailyMetricsData]);

  // Number of days the person worked (days with loaded metrics)
  const daysWorked = useMemo(() => {
    return dailyMetricsData.filter((m) => m.amount !== undefined && m.amount !== null).length;
  }, [dailyMetricsData]);

  // Average earnings per day for this person
  const averageEarnings = useMemo(() => {
    return daysWorked > 0 ? monthlySum / daysWorked : 0;
  }, [monthlySum, daysWorked]);

  // Function to determine class for summary cell
  const getSumCellClass = () => {
    if (person.nationality === 'Stranac') {
      if (monthlySum >= metrickePostavke.minMonthlyStranci) {
        return 'high-earning';
      } else if (monthlySum < metrickePostavke.minMonthlyStranci) {
        return 'low-earning';
      }
    } else if (person.nationality === 'Hrvat') {
      if (monthlySum >= metrickePostavke.minMonthlyHrvati) {
        return 'high-earning';
      } else if (monthlySum < metrickePostavke.minMonthlyHrvati) {
        return 'low-earning';
      }
    }

    return '';
  };

  return (
    <>
      <tr>
        <td className={isPinned ? 'selected-cell' : isRowSelected ? 'selected-cell' : ''} onClick={() => setIsRowSelected(!isRowSelected)}>
          <Tooltip content={person.comment || ''}>
            {person.name}
          </Tooltip>
        </td>
        <td className={isPinned ? 'selected-cell' : isRowSelected ? 'selected-cell' : ''}>
          <input
            type="checkbox"
            checked={isPinned}
            onChange={() => onTogglePin(person.id)}
            title="Pinaj osobu na vrh tablice"
          />
        </td>
        <td className={isPinned ? 'selected-cell' : isRowSelected ? 'selected-cell' : ''}>{person.riderID}</td>
        <td className={isPinned ? 'selected-cell' : isRowSelected ? 'selected-cell' : ''}>{person.city || 'N/A'}</td>
        <td className={isPinned ? 'selected-cell' : isRowSelected ? 'selected-cell' : ''}>{getFirmName(person.firmId)}</td>
        <td className={isPinned ? 'selected-cell' : isRowSelected ? 'selected-cell' : ''}>{person.nationality === 'Hrvat' ? 'H' : 'S'}</td>
        <td className={`${isPinned ? 'selected-cell' : isRowSelected ? 'selected-cell' : ''} ${getSumCellClass()}`}>
          <strong>{monthlySum.toFixed(2)}</strong>
        </td>
        <td className={isPinned ? 'selected-cell' : isRowSelected ? 'selected-cell' : ''}>
          <strong>{totalActualWorkingHours.toFixed(2)}</strong>
        </td>
        <td className={isPinned ? 'selected-cell' : isRowSelected ? 'selected-cell' : ''}>
          <strong>{averageEarnings.toFixed(2)}</strong>
        </td>
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
          const metric = dailyMetricsData.find((m) => m.day === day);
          const amount = metric ? parseFloat(metric.amount) : 0;
          const cellClass = metric ? getCellClass(amount) : '';
          const isWorkingDay = metric && (metric.fromTime || metric.toTime);

          // Get planned and actual working hours
          const plannedHours = metric ? metric.plannedWorkingHours : '';
          const actualHours = metric ? metric.actualWorkingHours : '';

          // Build title text for hover
          let titleText = '';
          if (isWorkingDay) {
            titleText += `Radno vrijeme: ${metric.fromTime} - ${metric.toTime}\n`;
          }
          if (plannedHours || actualHours) {
            if (plannedHours) {
              titleText += `Planirano radno vrijeme: ${plannedHours}\n`;
            }
            if (actualHours) {
              titleText += `Stvarno radno vrijeme: ${actualHours}`;
            }
          }
          titleText = titleText.trim();

          return (
            <td 
              key={day}
              className={`${isPinned ? 'selected-cell' : isRowSelected ? 'selected-cell' : ''} ${cellClass}`}
              onClick={() => openWorkModal(day)}
              title={titleText}
            >
              <b>{actualHours ? actualHours : '0'} </b> 
              ({metric ? amount.toFixed(2) : '0.00'})
              {isWorkingDay && <span className="work-dot">●</span>}
            </td>
          );
        })}
      </tr>
      

      {/* Modal for editing work details */}
      <Modal
        isOpen={workModalOpen}
        onRequestClose={closeWorkModal}
        contentLabel={`Radno vrijeme za dan ${currentDay}`}
        className="modal"
        overlayClassName="overlay"
      >
        <h2>Radno vrijeme za dan {currentDay}</h2>
        <form className="edit-work-details-form">
          
          <div className="form-group">
            <label>Planirano radno vrijeme:</label>
            <input
              type="text"
              value={workDetails.plannedWorkingHours}
              onChange={(e) =>
                setWorkDetails({ ...workDetails, plannedWorkingHours: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Stvarno radno vrijeme:</label>
            <input
              type="text"
              value={workDetails.actualWorkingHours}
              onChange={(e) =>
                setWorkDetails({ ...workDetails, actualWorkingHours: e.target.value })
              }
            />
          </div>
          <div className="button-group">
            <button type="button" className="save-button" onClick={handleSaveWorkDetails}>
              Spremi
            </button>
            <button type="button" className="cancel-button" onClick={closeWorkModal}>
              Odustani
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}

export default MetricsRow;
